import styled from "@emotion/styled";
import React, { useEffect, useRef } from "react";
import { ReactComponent as FieldIcon } from "@assets/icons/field.svg";
import { Geometry, Polygon, MultiPolygon } from "ol/geom";

interface IProps {
  id?: string | number;
  geometry?: Geometry;
  fillColor?: string;
}

const Root = styled.div`
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border-radius: 6px;
  margin-right: 16px;
`;

const CANVAS_SIZE = 46;
const PADDING = 5;
const EDGE_COLOR = "#68AD18";
const EDGE_WIDTH = 1.5;

function getPolygonRings(geometry: Geometry): number[][][][] {
  const type = geometry.getType();
  if (type === "Polygon") {
    return [(geometry as Polygon).getCoordinates()];
  }
  if (type === "MultiPolygon") {
    return (geometry as MultiPolygon).getCoordinates();
  }
  return [];
}

function drawGeometry(
  ctx: CanvasRenderingContext2D,
  geometry: Geometry,
  fillColor: string
) {
  const polygons = getPolygonRings(geometry);
  if (polygons.length === 0) return;

  const extent = geometry.getExtent();
  const [minX, minY, maxX, maxY] = extent;
  const geoW = maxX - minX || 1;
  const geoH = maxY - minY || 1;
  const drawArea = CANVAS_SIZE - PADDING * 2;
  const scale = Math.min(drawArea / geoW, drawArea / geoH);
  const offsetX = PADDING + (drawArea - geoW * scale) / 2;
  const offsetY = PADDING + (drawArea - geoH * scale) / 2;

  const toX = (x: number) => offsetX + (x - minX) * scale;
  const toY = (y: number) => CANVAS_SIZE - (offsetY + (y - minY) * scale);

  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  ctx.fillStyle = fillColor || "#ACDD73";
  ctx.strokeStyle = EDGE_COLOR;
  ctx.lineWidth = EDGE_WIDTH;
  ctx.lineJoin = "round";

  for (const rings of polygons) {
    for (let ri = 0; ri < rings.length; ri++) {
      const ring = rings[ri];
      if (ring.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(toX(ring[0][0]), toY(ring[0][1]));
      for (let i = 1; i < ring.length; i++) {
        ctx.lineTo(toX(ring[i][0]), toY(ring[i][1]));
      }
      ctx.closePath();
      if (ri === 0) {
        ctx.fill();
      }
      ctx.stroke();
    }
  }
}

const FieldPreview: React.FC<IProps> = ({ geometry, id, fillColor }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !geometry) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_SIZE * dpr;
    canvas.height = CANVAS_SIZE * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawGeometry(ctx, geometry, fillColor ?? "#ACDD73");
  }, [geometry, fillColor]);

  return (
    <Root>
      {geometry ? (
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
        />
      ) : (
        <FieldIcon />
      )}
    </Root>
  );
};
export default FieldPreview;
